import * as BrowserCheckUtils from './BrowserCheckUtils';

export class MarkdownSyntax {
    static readonly Header1: string = "# ";
    static readonly Header2: string = "## ";
    static readonly Header3: string = "### ";
    static readonly Code: string = "`";
    static readonly BulletedList: string = "- ";
    static readonly NumberedList: string = "1. ";
    static readonly TaskList: string = "- [ ] ";
    static readonly Bold: string = "**";
    static readonly Italic: string = "_";
    static readonly Quote: string = ">";
    static readonly Mention: string = "@";
    static readonly LinkWIT: string = "#";
    static readonly CodeBlock: string = "\n```\n";
}

export const HeaderMarkdownSyntaxes: string[] = [MarkdownSyntax.Header1, MarkdownSyntax.Header2, MarkdownSyntax.Header3];
export const ListMarkdownSyntaxes: string[] = [MarkdownSyntax.TaskList, MarkdownSyntax.BulletedList, MarkdownSyntax.NumberedList];

export const AllowedAttachmentExtensions = [".PNG", ".GIF", ".JPG", ".JPEG", ".DOCX", ".PPTX", ".XLSX", ".TXT", ".PDF", ".ZIP", ".GZ", ".LYR", ".MOV", ".MP4"];

export class MarkdownToolbarHelper {
    /**
     * This function inserts the insertedString param to the beginning of the line the cursor points to
     * @param textArea : the text component the toolbar is attached to.
     * @param insertedString :  the string to be inserted at line start
     * @param textChanged : callback supplied to the parent for some browsers
     * @param shouldToggle : should toggle the insert and removal of the insertedString
     * @param shouldReplaceIfHeader : when set would replace the header styling of the line
     * @param shouldReplaceIfList : when set would replace the list styling of the line
     */
    public static insertTextAtStartOfEachLine(
        textArea: HTMLTextAreaElement,
        insertedString: string,
        textChanged?: (text: string, selectionStart: number, selectionEnd: number) => void,
        shouldToggle?: boolean,
        shouldReplaceIfHeader?: boolean,
        shouldReplaceIfList?: boolean,
    ): void {
        //let textArea = this.props.getTextArea();
        if (!textArea) {
            return;
        }

        if (BrowserCheckUtils.isEdge()) {
            /**
             * Hack: textArea.selectionEnd is not populated correctly until it has focus once.
             * https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8018707/
             */
            textArea.focus();
        }

        const currentText = textArea.value;
        let selectionStart = textArea.selectionStart;
        let selectionEnd = textArea.selectionEnd;

        // Hack for IE: If the last character in the textArea is newline and cursor is at the end of the text,
        // selectionStart and selectionEnd are greater than length of the text. The newText calculated will be incorrect.
        if (BrowserCheckUtils.isIE() && textArea.textContent != null && textArea.textContent.length !== 0) { //chrome has this value set as zero always
            if (selectionStart > textArea.textContent.length) {
                selectionStart = textArea.textContent.length;
            }
            if (selectionEnd > textArea.textContent.length) {
                selectionEnd = textArea.textContent.length;
            }
        }

        // move the cursor to the start of the current line for things like lists and quotes
        const startOfLine = currentText.slice(0, selectionStart).lastIndexOf("\n") + 1;
        textArea.selectionStart = selectionStart = startOfLine;

        const selectedText = currentText.substring(selectionStart, selectionEnd);
        let lines = selectedText.split("\n");
        shouldReplaceIfHeader = shouldReplaceIfHeader && HeaderMarkdownSyntaxes.indexOf(insertedString) !== -1;
        shouldReplaceIfList = shouldReplaceIfList && ListMarkdownSyntaxes.indexOf(insertedString) !== -1;
        lines = lines.map(l => {
            // This condition is to avoid adding markdown text if no character is selected in last line
            if (!l && selectionStart !== selectionEnd) {
                return l;
            }

            const matchedLineHeaderMarkdown: string | null = this._getMarkdownSyntaxIfPresent(l, HeaderMarkdownSyntaxes);
            const matchedLineListMarkdown: string | null = this._getMarkdownSyntaxIfPresent(l, ListMarkdownSyntaxes);
            if (shouldToggle && (insertedString === matchedLineHeaderMarkdown
                || insertedString === matchedLineListMarkdown)) {
                return l.substring(insertedString.length, l.length);
            } else if (shouldReplaceIfHeader && matchedLineHeaderMarkdown) {
                return insertedString + l.substring(matchedLineHeaderMarkdown.length, l.length);
            } else if (shouldReplaceIfList && matchedLineListMarkdown) {
                return insertedString + l.substring(matchedLineListMarkdown.length, l.length);
            } else {
                return insertedString + l;
            }
        });
        const newText = lines.join("\n");

        textArea.focus();
        if (this.browserSupportsInsertText()) {
            // If the browser supports it, insertText is a better way to programatically change the text which supports undo/redo
            // This will also trigger any events on the parent control as if the user typed in this text
            document.execCommand("insertText", false, newText);
        } else {
            const firstPart = currentText.substring(0, selectionStart);
            const endPart = currentText.substring(selectionEnd);
            const newCursorPosition = firstPart.length + newText.length;

            // otherwise, the parent control needs to deal with the new text
            if (textChanged) {
                textChanged(firstPart + newText + endPart, newCursorPosition, newCursorPosition);
            }
        }
    }

    public static insertTextAtStartOfLineOrDoc(
        getTextArea: () => HTMLTextAreaElement,
        commandSnippet: string,
        textChanged: (text: string, selectionStart: number, selectionEnd: number) => void,
        insertAtStartOfDoc?: boolean
    ): void {
        const textArea: HTMLTextAreaElement = getTextArea();

        if (textArea && BrowserCheckUtils.isEdge()) {
            /**
             * Hack: textArea.selectionEnd is not populated correctly until it has focus once.
             * https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8018707/
             */
            textArea.focus();
        }

        if (textArea) {
            if (insertAtStartOfDoc) {
                textArea.selectionStart = textArea.selectionEnd = 0;
            } else if (textArea.selectionStart !== textArea.selectionEnd) {
                // When text is selected, put the commandSnippet at the selectionStart only.
                // Hence making sure the selectionEnd is same as selectedStart.
                textArea.selectionEnd = textArea.selectionStart;
            }
        }

        this.insertTextAtStartOfEachLine(textArea, commandSnippet + "\n", textChanged);
    }

    /**
     * This function wraps the wrapperString param around the selected text.
     * @param textArea - the text component the toolbar is attached to.
     * @param wrapperString - the strign to be wrapped around the selected text.
     * @param textChanged - callback supplied to the parent for some browsers
     */
    public static wrapText(textArea: HTMLTextAreaElement, wrapperString: string,
        textChanged?: (text: string | null, selectionStart: number, selectionEnd: number) => void): void {
        if (!textArea) {
            return;
        }
        
        let cursorPosition = textArea.selectionStart || 0;
        let selectionStart = textArea.selectionStart || 0;
        let selectionEnd = textArea.selectionEnd || 0;
        const currentText = textArea.value;
        const wrapperLength = wrapperString.length;
        const hasSelection = selectionStart !== selectionEnd;
        const trimmedCodeBlockString = MarkdownSyntax.CodeBlock.trim();
        const trimmedCodeBlockLength = trimmedCodeBlockString.length;

        // if some text is selected then we want to wrap the selected text minus outside whitespace
        // or if they have the cursor in the middle of the word, we want to wrap the word
        if (!hasSelection) {
            // expand from cursor position to create a selection
            const expandResults = this.expandCursorToWord(currentText, selectionStart);
            selectionStart = expandResults.start;
            selectionEnd = expandResults.endExclusive;
        }
        else {
            // if we do have a selection, trim beginning/trailing whitespace
            // eslint-disable-next-line security/detect-object-injection
            while (selectionStart < selectionEnd && /\s/.test(currentText[selectionStart])) {
                selectionStart++;
            }

            while (selectionEnd > selectionStart && /\s/.test(currentText[selectionEnd - 1])) {
                selectionEnd--;
            }

            // if the selection has an incomplete wrapper string for toggling, remove the wrapper string selection
            if (wrapperString === MarkdownSyntax.CodeBlock) {
                if (currentText.substring(selectionStart, selectionStart + trimmedCodeBlockLength) === trimmedCodeBlockString) {
                    selectionStart += trimmedCodeBlockLength + 1;
                }

                if (currentText.substring(selectionEnd - trimmedCodeBlockLength, selectionEnd) === trimmedCodeBlockString) {
                    selectionEnd -= trimmedCodeBlockLength + 1;
                }
            }

            // then expand to grab the wrappers if they already exists
            if (selectionStart - wrapperLength >= 0 &&
                currentText.substr(selectionStart - wrapperLength, wrapperLength) === wrapperString) {
                selectionStart -= wrapperLength;
            }

            if (currentText.substr(selectionEnd, wrapperLength) === wrapperString) {
                selectionEnd += wrapperLength;
            }
        }

        const firstPart = currentText.substring(0, selectionStart);
        const endPart = currentText.substring(selectionEnd);
        const middlePart = currentText.substring(selectionStart, selectionEnd);

        let replacementText: string;

        // if the wrapper is already present, remove it. Otherwise, add it.
        // if you place your cursor over a word, for example, and press bold repeatidly, it should toggle bold for that word
        const alreadyMatches = wrapperString === middlePart.substr(0, wrapperLength) &&
            wrapperString === middlePart.substr(middlePart.length - wrapperLength);
        if (alreadyMatches) {
            replacementText = middlePart.substr(wrapperLength, middlePart.length - 2 * (wrapperLength));
            cursorPosition -= wrapperLength;

            if (hasSelection) {
                selectionEnd -= 2 * wrapperLength;
            }
            else {
                selectionStart -= wrapperLength;
                selectionEnd -= wrapperLength;
            }
        }
        else {
            replacementText = wrapperString + middlePart + wrapperString;
            cursorPosition += wrapperLength;
            selectionStart += wrapperLength;
            selectionEnd += wrapperLength;
        }

        if (!hasSelection) {
            selectionStart = selectionEnd = cursorPosition;
        }

        textArea.focus();
        if (this.browserSupportsInsertText()) {
            // If the browser supports it, insertText is a better way to programatically change the text which supports undo/redo
            // This will also trigger any events on the parent control as if the user typed in this text
            textArea.selectionStart = firstPart.length;
            textArea.selectionEnd = firstPart.length + middlePart.length;

            document.execCommand("insertText", false, replacementText);

            textArea.selectionStart = selectionStart;
            textArea.selectionEnd = selectionEnd;

            if (textChanged) {
                textChanged(null, selectionStart, selectionEnd);
            }
        }
        else {
            const allText: string = firstPart + replacementText + endPart;
            const newCursorPosition = firstPart.length + replacementText.length;

            textArea.value = allText;
            textArea.selectionStart = textArea.selectionEnd = newCursorPosition;

            textArea.selectionStart = selectionStart;
            textArea.selectionEnd = selectionEnd;

            if (textChanged) {
                textChanged(allText, selectionStart, selectionEnd);
            }
        }
    }

    public static insertTextAtCursor(textArea: HTMLTextAreaElement, text: string): void {
        if (this.browserSupportsInsertText()) {
            document.execCommand("insertText", false, text);
        }
        else {
            const cursorPosition: number = textArea.selectionStart || 0;
            const allText: string = textArea.value;
            textArea.value = allText.slice(0, cursorPosition) + text + allText.slice(cursorPosition);
        }
    }

    public static replaceTextInRange(textArea: HTMLTextAreaElement, range: SelectionRange, text: string): void {
        if (this.browserSupportsInsertText()) {
            textArea.selectionStart = range.start;
            textArea.selectionEnd = range.endExclusive;
            document.execCommand("insertText", false, text);
        }
        else {
            const allText: string = textArea.value;
            textArea.value = allText.slice(0, range.start) + text + allText.slice(range.endExclusive);
        }
    }

    /**
     * This function virtually selects the whole word the cursor is in.
     * @param text - the text present in the textarea component
     * @param cursorPosition
     */
    public static expandCursorToWord(text: string, cursorPosition: number): SelectionRange {
        const left = text.slice(0, cursorPosition + 1).search(/\S+$/);
        const right = text.slice(cursorPosition).search(/\s/);
        let start, end;

        if (left < 0) {
            start = cursorPosition;
        } else {
            start = left;
        }

        if (right < 0) {
            end = text.length;
        }
        else {
            end = right + cursorPosition;
        }

        start = Math.min(Math.max(0, start), text.length);
        end = Math.min(Math.max(0, end), text.length);

        return { start: start, endExclusive: end };
    }

    /**
     * Gets markdown syntax if present in the beginning of the line from given list of markdown syntaxes
     * @param {string} line - where to search markdown syntax
     * @param {string[]} markdownSyntaxesList - list from which markdown syntaxes are searched in given line
     * @returns - markdown syntax matched (from beginning of line) from given list of syntaxes or null otherwise
     */
    private static _getMarkdownSyntaxIfPresent(line: string, markdownSyntaxesList: string[]): string | null {
        for (const markdownSyntax of markdownSyntaxesList) {
            if (line.indexOf(markdownSyntax) === 0) {
                return markdownSyntax;
            }
        }

        return null;
    }

    /**
     * Tests whether insertText is supported in the browser.
     * insertText programatically programatically change the text which supports undo/redo
    */
    public static browserSupportsInsertText(): boolean {
        // ie does not support it and correctly reports that it does not support it.
        // firefox claims to support it... but lies.
        // see https://bugzilla.mozilla.org/show_bug.cgi?id=1220696
        if (BrowserCheckUtils.isFirefox() || !document.queryCommandSupported("insertText")) {
            return false;
        }

        return true;
    }
}


export interface SelectionRange {
    start: number;
    endExclusive: number;
}